import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useBoxPrograms } from "@/modules/boxProgrammation/hooks/useBoxProgram"
import { useBoxProgrammationSidebar } from "@/modules/boxProgrammation/hooks/useBoxProgramSidebar"
import { cn } from "@/modules/lib/utils"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { useWindowSize } from "@modules/utils/hooks/useWindowsSize"
import { ChevronDown, Plus } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useParams } from "react-router"
import styled from "styled-components"

type BoxPlanningHeaderCenterViewProps = {
  className?: string
} & React.ComponentProps<"div">

export function BoxPlanningHeaderCenterView({ className, ...props }: BoxPlanningHeaderCenterViewProps) {
  const { boxProgramId } = useParams()
  const [areActivitiesExpanded, setAreActivitiesExpanded] = useState(false)
  const [shouldDisplayShowMore, setShouldDisplayShowMore] = useState(false)
  const { data: boxProgrammations } = useBoxPrograms()
  const { navigateToBoxProgrammationCreation } = useBoxProgrammationSidebar()
  const boxNavigate = useBoxNavigate()
  const location = useLocation()
  const { selectedDate } = useCalendar()
  const { width } = useWindowSize()
  const canCreateActivity = useBoxStaffPermission(BoxStaffPermissions.CREATE_ACTIVITY)

  const listRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const toggleActivityList = () => setAreActivitiesExpanded((value) => !value)

  const navigateToActivity = (activityId: string) => {
    if (boxProgramId === activityId) {
      boxNavigate(Path.Box.Planning, {
        state: { selectedDate: selectedDate?.toString() ?? "" },
      })
      return
    } else {
      if (boxProgramId) {
        if (location.pathname.includes(Path.Box.ProgrammationTemplate(boxProgramId))) {
          boxNavigate(Path.Box.ProgrammationTemplate(activityId))
          return
        } else if (location.pathname.includes(Path.Box.ProgrammationSessions(boxProgramId))) {
          boxNavigate(Path.Box.ProgrammationSessions(activityId), {
            state: { selectedDate: selectedDate?.toString() ?? "" },
          })
          return
        }
      }
    }

    boxNavigate(Path.Box.ProgrammationDetails(activityId), {
      state: { selectedDate: selectedDate?.toString() ?? "" },
    })
  }

  // Computes the "See more" position related to its container, as the overflow is changing
  const seeMoreBottomPosition = useMemo(() => {
    return areActivitiesExpanded && containerRef.current
      ? containerRef.current.clientHeight - containerRef.current.scrollHeight
      : 0
  }, [areActivitiesExpanded, containerRef.current?.scrollHeight, containerRef.current?.clientHeight, width])

  const checkShowMore = () => {
    setShouldDisplayShowMore(listRef.current ? listRef.current.scrollHeight > 134 : false) // 118px is the height of 3 rows + gap
  }

  useEffect(() => {
    checkShowMore()
  }, [listRef.current, width])

  useEffect(() => {
    if (areActivitiesExpanded) setAreActivitiesExpanded(false)
  }, [width])

  return (
    <div
      className={cn(
        "relative z-50 flex h-50 w-full flex-col gap-5 rounded-4xl bg-surface-foreground",
        areActivitiesExpanded ? "overflow-visible" : "overflow-hidden p-4",
        className,
      )}
      {...props}
      ref={containerRef}
    >
      <Text fontStyle="TM">{word("box.planning.filter.activities.title")}</Text>

      <div
        ref={listRef}
        className="z-3 flex flex-row flex-wrap items-center gap-2 rounded-4xl bg-surface-foreground px-4.5 pb-4.5"
      >
        {boxProgrammations.map((activity, index) => (
          <ProgrammationItemButton
            key={index}
            isSelected={boxProgramId === activity.id}
            onClick={() => navigateToActivity(activity.id)}
            text={activity.name}
            onLoaded={() => {
              if (index === boxProgrammations.length - 1) checkShowMore()
            }} // Once the last is loaded, check if we need to display the "See more" button
          />
        ))}

        {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
        {canCreateActivity && (
          <Button variant="underline" onClick={navigateToBoxProgrammationCreation}>
            <Plus />
            {word("box.planning.empty.create_activity")}
          </Button>
        )}
      </div>

      {shouldDisplayShowMore && (
        <div
          onClick={toggleActivityList}
          className="absolute z-5 mt-2 flex w-full cursor-pointer flex-row items-center justify-center gap-2 pb-1"
          style={{
            bottom: seeMoreBottomPosition,
          }}
        >
          <Text fontStyle="BS">
            {areActivitiesExpanded ? word("box.planning.seeLess") : word("box.planning.seeMore")}
          </Text>

          <ChevronDown
            className={cn("size-4 transition-transform duration-200", areActivitiesExpanded && "rotate-180")}
          />
        </div>
      )}
      <GradientOverlay $bottom={seeMoreBottomPosition} />
    </div>
  )
}

function ProgrammationItemButton({
  text,
  onClick,
  isSelected,
  onLoaded,
}: {
  text: string
  onClick: () => void
  isSelected: boolean
  onLoaded: () => void
}) {
  useEffect(() => {
    onLoaded()
  }, [])

  return (
    <>
      {text.length > 32 ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button variant={isSelected ? "primary" : "outline"} size="sm" onClick={onClick}>
                <span className="max-w-45 truncate text-start">{text}</span>
              </Button>
            }
          />
          <TooltipContent side="bottom" className="w-fit max-w-60 wrap-break-word whitespace-normal">
            {text}
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button variant={isSelected ? "primary" : "outline"} size="sm" onClick={onClick}>
          {text}
        </Button>
      )}
    </>
  )
}

const GradientOverlay = styled.div<{ $bottom: number }>`
  background: linear-gradient(180deg, rgba(28, 28, 28, 0) 30%, var(--neutral-100) 83%);
  border-width: 0px;
  height: 36px;
  border-bottom-left-radius: 33px;
  border-bottom-right-radius: 33px;
  position: absolute;
  left: 0;
  right: 0;
  bottom: ${({ $bottom }) => `${$bottom.toString()}px`};
  pointer-events: none;
  z-index: 4;
`
